// external
import React, { useState, useContext, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import uniqid from "uniqid";

// components
import CustomSelect from "components/partials/custom-select/custom-select";
import ProgressTracker from "components/ProgressTracker";
import StepNavigation from "components/StepNavigation";
import FaoBuilderHeader from "./fao-layout/fao-builder-header";
import Loader from "components/partials/loader/loader";
import CustomButton from "components/partials/custom-button/custom-button";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { ClientContext } from "context/ClientContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";

// assets
import Icons from "assets/icons";

// css/styling
import { STYLES, mergeStyles } from "./constants/styles.constants";
import useModelRefresh from "./hooks/use-model-refresh";

const selectStyle = {
  control: mergeStyles(STYLES.select.control, (provided) => ({
    width: "100%",
  })),
  option: STYLES.select.option,
  menuPortal: STYLES.select.menuPortal,
};
/*
TODO:
  - clean up this component, way too long right now
  - implement the select funds using TableComponents, this will help create a search bar and filters 
  - might be helpful to show a summary window of selected funds
*/
function FaoCreateImport() {
  const navigate = useNavigate();
  const [dsTableName, setDsTableName] = useState("");
  const [loadedDsTableNames, setLoadedDsTableNames] = useState([]);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const { currentClientData } = useContext(ClientContext);
  const { modelConfig, updateModelConfig, formatDate, refreshStatus } =
    useContext(FaoBuilderContext);
  const { handleRefresh } = useModelRefresh();
  const [loadedFunds, setLoadedFunds] = useState([]);
  const [componentStatus, setComponentStatus] = useState({
    isSaved: false,
    isSaving: false,
    isLoading: true,
  });
  const { isSaved, isSaving, isLoading } = componentStatus;
  const [refreshedDate, setRefreshedDate] = useState("");
  const uniqueId = uniqid();
  const fundTableHeaders = [
    "Select",
    "Fund Name",
    "Fund Key",
    "Source",
    "Type",
    "SubGroup",
    "Institutional Aid",
  ];
  useEffect(() => {
    setComponentStatus((prev) => ({
      ...prev,
      isSaved: false,
    }));
  }, [dsTableName, selectedFunds]);
  useEffect(() => {
    const fetchDsTableNames = async () => {
      try {
        const toastMessages = {
          pending: "Fetching Data Science Tables",
          success: "SUCCESS - Data Science Tables Fetched",
          error: "FAILURE",
        };

        const promise = apiRoot.get(`/api/client-funds/executeDsTableName`);
        const response = await toast.promise(promise, toastMessages);
        setLoadedDsTableNames(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchDsTableNames();
  }, []);

  const handleFundSelection = (fundId) => {
    setSelectedFunds((prev) => {
      if (prev.includes(fundId)) {
        return prev.filter((id) => id !== fundId);
      }
      return [...prev, fundId];
    });
  };
  const fetchRefreshedDate = useCallback(async () => {
    try {
      const refreshedInfo = await apiRoot.get(
        `/api/fao-imports/get-last-refreshed`,
        {
          params: { modelId: modelConfig.id },
        }
      );
      const refreshedDate = refreshedInfo.data[0]
        ? refreshedInfo.data[0]["last_altered"]
        : "";
      formatDate(refreshedDate, setRefreshedDate);
    } catch (error) {
      console.log(error);
    }
  }, [formatDate, modelConfig.id]);

  // fetch the last refreshed date if dataset is not currently refreshing and the import is saved at least once
  useEffect(() => {
    if (
      !refreshStatus[modelConfig.id]?.isRefreshing &&
      modelConfig.configs.importConfig !== ""
    ) {
      fetchRefreshedDate();
    }
  }, [fetchRefreshedDate, refreshStatus, modelConfig]);

  useEffect(() => {
    const handleLoad = async () => {
      if (modelConfig?.id && currentClientData?.schoolKey) {
        console.log("Fetching existing import config...");
        const existingImportId = modelConfig.configs["importConfig"];

        console.log("Existing import ID:", existingImportId);

        if (existingImportId) {
          try {
            //Fetching existing import config
            const toastId = toast.loading("Loading Import Configuration...");
            const importData = await apiRoot.get(
              `/api/fao-imports/${existingImportId}`
            );
            fetchRefreshedDate();
            // Set the DS Table Name
            if (importData.data.dsTableName) {
              setDsTableName({
                value: importData.data.dsTableName,
                label: importData.data.dsTableName,
              });
            }
            // Set the selected funds
            if (importData.data.specialFunds) {
              setSelectedFunds(importData.data.specialFunds);
            }

            // Update toast to success
            toast.update(toastId, {
              render: "SUCCESS - Import Configuration Loaded",
              type: "success",
              isLoading: false,
              autoClose: 3000,
            });
          } catch (error) {
            console.error("Error loading import:", error);
            toast.error("Failed to load import configuration");
          }
        } else {
          console.log("No existing import ID found");
        }
      } else {
        console.log("Missing required data:", {
          hasModelConfig: !!modelConfig,
          hasSchoolKey: !!currentClientData?.schoolKey,
        });
      }
      try {
        const toastMessages = {
          pending: "Fetching Funds",
          success: "SUCCESS - Funds Fetched",
          error: "FAILURE",
        };

        const promise = apiRoot.get(`/api/client-funds/executeFunds`, {
          params: {
            schoolKey: currentClientData.schoolKey,
          },
        });
        const response = await toast.promise(promise, toastMessages);
        const sortedFunds = response.data.sort((a, b) =>
          a[5] > b[5] ? 1 : -1
        );
        setLoadedFunds(sortedFunds);
      } catch (error) {
        setLoadedFunds(null);
      }
      setComponentStatus((prev) => ({
        ...prev,
        isLoading: false,
      }));
    };
    handleLoad();
  }, []);

  const postImport = async () => {
    if (!dsTableName) {
      toast.error("Please select a DS Table Name");
      return;
    }
    setComponentStatus((prev) => ({
      ...prev,
      isSaving: true,
    }));
    try {
      // Show loading toast
      const toastId = toast.loading("Saving FAO Import...");

      const existingImportId = modelConfig.configs["importConfig"];
      console.log(modelConfig.configs);

      if (existingImportId !== "") {
        const importData = {
          id: existingImportId,
          schoolKey: currentClientData.schoolKey,
          dsTableName: dsTableName.value,
          specialFunds: selectedFunds,
        };
        console.log(importData);
        await apiRoot.patch(`/api/fao-imports`, importData, {
          params: { modelId: modelConfig.id },
        });
      } else {
        const importData = {
          id: uniqueId,
          schoolKey: currentClientData.schoolKey,
          dsTableName: dsTableName.value,
          specialFunds: selectedFunds,
        };
        console.log(importData);
        await apiRoot.post(`/api/fao-imports`, importData, {
          params: { modelId: modelConfig.id },
        });
        // 2. Update model config with import ID
        await updateModelConfig("importConfig", uniqueId);
      }

      // Update toast to success
      toast.update(toastId, {
        render: "SUCCESS - FAO Import Saved",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // 3. Navigate only after successful save
    } catch (error) {
      console.error("Error saving import:", error);
      toast.error(error.message || "Failed to save import");
    }
    setComponentStatus((prev) => ({
      ...prev,
      isSaving: false,
      isSaved: true,
    }));
    handleRefresh(`/api/fao-imports/refresh-if-needed`, modelConfig);
  };

  const handleNewRefresh = () => {
    handleRefresh(`/api/fao-imports/refresh`, modelConfig);
  };
  return (
    <div className={`flex h-full w-full flex-col`}>
      <div className="mx-auto flex w-full flex-col">
        <ToastContainer
          style={{ width: "500px", fontSize: "16px" }}
          progressStyle={{ backgroundColor: "#b6de3a" }}
        />
        <FaoBuilderHeader
          modelName={modelConfig.versionName}
          modelId={modelConfig.id}
        />
        <ProgressTracker />
        {/* DS Table Name Input with Dropdown and Autocomplete */}
        {isLoading ? (
          <Loader />
        ) : (
          <div className="flex flex-col">
            <div className="flex justify-between">
              <label htmlFor="dstablename" className={`${STYLES.label} mb-2`}>
                DS Table Name
              </label>
              <CustomButton
                id="dstablename"
                variant="inverse"
                iconBefore={<Icons.Reset customClass="h-4 w-4 rotate-45" />}
                size="sm"
                onClick={handleNewRefresh}
              >
                Refresh Dataset
              </CustomButton>
            </div>
            <CustomSelect
              labelInTop={true}
              options={loadedDsTableNames.map((item) => ({
                value: item[0],
                label: item[0],
              }))}
              value={dsTableName}
              required={true}
              disabled={isLoading}
              isLoading={isLoading}
              onChange={(selected) => setDsTableName(selected)}
              extraStyles={() => selectStyle}
            />

            {/* Fund Selection */}
            <div className="mt-4 flex w-full flex-col justify-center gap-2">
              <label htmlFor="fundsTable" className={STYLES.label}>
                Select Special Funds
              </label>
              <div className="max-h-[50vh] overflow-y-auto rounded-md border border-gray-300">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      {fundTableHeaders.map((item, index) => (
                        <th
                          key={index}
                          className="font-family text-md px-2 py-2 text-left font-semibold text-blue-03"
                        >
                          {item}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadedFunds &&
                      loadedFunds.map((item, index) => (
                        <tr key={index} className="border-t border-gray-300">
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={selectedFunds.includes(item?.[0])}
                              onChange={() => handleFundSelection(item?.[0])}
                              className={STYLES.checkbox.input}
                            />
                          </td>
                          <td className="px-4 py-2 text-blue-03">
                            {item?.[5]}
                          </td>
                          <td className="px-4 py-2 text-blue-03">
                            {item?.[0]}
                          </td>
                          <td className="px-4 py-2 text-blue-03">
                            {item?.[2]}
                          </td>
                          <td className="px-4 py-2 text-blue-03">
                            {item?.[3]}
                          </td>
                          <td className="px-4 py-2 text-blue-03">
                            {item?.[4]}
                          </td>
                          <td className="px-4 py-2 text-blue-03">
                            {item?.[6]}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className={mergeStyles(STYLES.paragraph, "mt-2")}>
              {`Last Refreshed: ${
                !refreshedDate
                  ? "Dataset not created yet for this model"
                  : `${refreshedDate}`
              }`}
            </p>
            <div className="mt-1 flex">
              <Icons.Info customClass="h-4 w-4 text-gray-500 mr-1 mt-1"></Icons.Info>
              <p className="text-gray-500">
                This page allows you to refresh the underlying dataset when
                census updates occur or funnel data changes. The dataset also
                automatically refreshes whenever you save modified inputs,
                eliminating the need for manual refreshes in most cases.
              </p>
            </div>
          </div>
        )}
        <StepNavigation
          onClickBack={() =>
            navigate(`../fao-builder/${modelConfig.id}/metadata`)
          }
          isSaving={isSaving}
          isSaved={isSaved}
          onClickNext={() =>
            navigate(`../fao-builder/${modelConfig.id}/regime-param`)
          }
          onClickSave={postImport}
          disabledNext={!isSaved}
        ></StepNavigation>
      </div>
    </div>
  );
}

export default FaoCreateImport;
